import type { CSSProperties, HTMLAttributes, ReactElement } from 'react';

import { classNames } from '@m1work/shared-utils';

import styles from './Text.module.scss';

export type TextSize = 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl';
export type TextWeight = 'snug' | 'normal' | 'medium' | 'semibold' | 'bold';
export type TextColor = 'default' | 'muted' | 'primary' | 'error' | 'accent';
export type TextAlign = 'left' | 'center' | 'right';
export type TextLineHeight = 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';

export interface TextProps extends HTMLAttributes<HTMLElement> {
    /** HTML element to render. @default 'p' */
    as?: 'p' | 'span' | 'label' | 'div';
    /** Font size from the type scale. @default 'base' */
    size?: TextSize;
    /** Font weight. @default 'normal' */
    weight?: TextWeight;
    /** Text colour role. @default 'default' */
    color?: TextColor;
    /** Horizontal alignment; inherited when not set. */
    align?: TextAlign;
    /** Line height; the size's own when not set. */
    lineHeight?: TextLineHeight;
    /** Cut a single line with an ellipsis. @default false */
    truncate?: boolean;
    /** Cut after this many lines with an ellipsis. */
    lineClamp?: number;
    /** The `for` attribute when rendered `as="label"`. */
    htmlFor?: string;
}

/**
 * Body text with sizes, weights and colours from the design tokens. Use it for every piece of text that isn't a
 * heading, so typography stays consistent.
 *
 * @example
 * <Text size="sm" color="muted" lineClamp={2}>
 *     {description}
 * </Text>
 */
export function Text({
    as: Component = 'p',
    size = 'base',
    weight = 'normal',
    color = 'default',
    align,
    lineHeight,
    truncate,
    className,
    lineClamp,
    style,
    ...props
}: TextProps): ReactElement {
    const lineClampStyle: CSSProperties | undefined = lineClamp ? { ...style, WebkitLineClamp: lineClamp } : style;

    return (
        <Component
            className={classNames(
                styles.text,
                styles[`size-${size}`],
                styles[`weight-${weight}`],
                styles[`color-${color}`],
                align && styles[`align-${align}`],
                lineHeight && styles[`line-height-${lineHeight}`],
                truncate && styles.truncate,
                lineClamp && styles['line-clamp'],
                className,
            )}
            style={lineClampStyle}
            {...props}
        />
    );
}
